import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DeviceListTable } from '~/features/devices/components/DeviceListTable';
import { useGetCampaignDeviceInfoMutation } from '~/api/campaignStatistics/CampaignStatistics.api';
import TableSearch from '@rio-cloud/rio-uikit/lib/es/TableSearch';
import { ALL, deviceStatusTypeOptions } from '~/features/base/constants/filterOptions';
import SelectOTA from '~/features/base/components/forms/ota/SelectOTA';
import { selectionByValue } from '~/features/base/utils/selectUtils';
import DatePicker from '@rio-cloud/rio-uikit/lib/es/DatePicker';
import moment, { Moment } from 'moment';
import { TableLoader } from '~/features/base/components/TableLoader';
import { devicesDescriptors } from '~/features/campaigns/constants/devicesDescriptors';
import { installationStatusOptions } from '~/features/campaigns/constants/installationStatusOptions';
import { useDispatch } from 'react-redux';
import { setShowDeviceDetailsSidebar } from '~/features/devices/actions/ui/controlDeviceDetailsActions';
import NotFoundState from '@rio-cloud/rio-uikit/lib/es/NotFoundState';
import LoadMoreButton from '@rio-cloud/rio-uikit/lib/es/LoadMoreButton';
import { createVinOrSerialNumberFilters } from '~/features/devices/utils/searchUtils';
import { useHistory, useLocation } from 'react-router-dom';
import { parseQuery } from '~/features/base/utils/query';

const DEVICE_COLUMNS = ['intl-msg:stage', 'intl-msg:deviceId', 'intl-msg:VIN', 'intl-msg:deviceHealthState', 'intl-msg:deviceHealthState', 'intl-msg:InstallationStatus', 'intl-msg:lastCheckForUpdateTimestamp'];

interface CampaignDevicesProps {
    stageId?: number;
    campaignId?: number;
}

const CampaignDevices: React.FC<CampaignDevicesProps> = ({ stageId, campaignId }) => {
    const history = useHistory();
    const location = useLocation();
    const query = parseQuery(location.search);

    const [healthOption, setHealthOption] = useState(query.healthStatus || ALL);
    const [installationOption, setInstallationOption] = useState(query.installationStatus || ALL);
    const [lastSeenDate, setLastSeenDate] = useState<Moment | undefined>(query.lastSeenDate ? moment(query.lastSeenDate) : undefined);
    const [searchValue, setSearchValue] = useState(query.search || '');
    const [campaignCurrentPage, setCampaignCurrentPage] = useState(0);
    const [allCampaignDevices, setAllCampaignDevices] = useState<any[]>([]);
    const intl = useIntl();

    const updateUrl = (filters: any) => {
        const newQuery = { ...query, ...filters };
        Object.keys(newQuery).forEach(key => {
            if (newQuery[key] === undefined || newQuery[key] === ALL || newQuery[key] === '') {
                delete newQuery[key];
            }
        });
        const searchParams = new URLSearchParams(newQuery).toString();
        history.replace(`${location.pathname}${searchParams ? `?${searchParams}` : ''}`);
    };


    const [getCampaignDeviceInfo, {
        data: campaignDevices,
        isLoading: campaignLoading
    }] = useGetCampaignDeviceInfoMutation();

    React.useEffect(() => {
        const searchFilter = createVinOrSerialNumberFilters(searchValue);
        if (campaignId) {
            setCampaignCurrentPage(0);
            setAllCampaignDevices([]);
            getCampaignDeviceInfo({
                campaignId,
                filterCriteria: {
                    stageId: stageId || undefined,
                    lastCheckForUpdateAfter: lastSeenDate?.toISOString(),
                    healthStatuses: healthOption !== ALL ? [healthOption] : undefined,
                    installationStatuses: installationOption !== ALL ? [installationOption] : undefined,
                    ...searchFilter,
                    pageNumber: 0,
                    pageSize: 10
                }
            });
        }
    }, [campaignId, stageId, lastSeenDate, healthOption, installationOption, getCampaignDeviceInfo, searchValue]);

    React.useEffect(() => {
        if (campaignDevices?.content) {
            if (campaignCurrentPage === 0) {
                setAllCampaignDevices(campaignDevices.content);
            } else {
                setAllCampaignDevices(prev => [...prev, ...campaignDevices.content]);
            }
        }
    }, [campaignDevices, campaignCurrentPage]);

    const controlDevices = {
        ...campaignDevices,
        content: allCampaignDevices
    };

    const isLoading = campaignLoading;

    const onDeviceStatusTypeChange = (selectedOption: any) => {
        const value = selectedOption?.value || ALL;
        setHealthOption(value);
        updateUrl({ healthStatus: value });
    };

    const healthOptions = selectionByValue(healthOption)(deviceStatusTypeOptions);
    const installationOptions = selectionByValue(installationOption)(installationStatusOptions);

    const onInstallationStatusChange = (selectedOption: any) => {
        const value = selectedOption?.value || ALL;
        setInstallationOption(value);
        updateUrl({ installationStatus: value });
    };

    const onLastSeenChange = (value: string | Moment) => {
        const momentValue = value as Moment;
        setLastSeenDate(momentValue);
        updateUrl({ lastSeenDate: momentValue && moment.isMoment(momentValue) ? momentValue.toISOString() : undefined });
    };

    const clearFilters = () => {
        setHealthOption(ALL);
        setInstallationOption(ALL);
        setLastSeenDate(undefined);
        setSearchValue('');
        history.replace(location.pathname);
    };

    const handleLoadMore = () => {
        const nextPage = campaignCurrentPage + 1;
        setCampaignCurrentPage(nextPage);
        getCampaignDeviceInfo({
            campaignId,
            filterCriteria: {
                stageId: stageId || undefined,
                lastCheckForUpdateAfter: lastSeenDate?.toISOString(),
                healthStatuses: healthOption !== ALL ? [healthOption] : undefined,
                installationStatuses: installationOption !== ALL ? [installationOption] : undefined,
                pageNumber: nextPage,
                pageSize: 10
            }
        });
    };

    const dispatch = useDispatch();
    const renderContent = () => {
        if (isLoading && campaignCurrentPage === 0) {
            return <TableLoader labels={DEVICE_COLUMNS} nLines={5}/>;
        }

        if (!controlDevices?.content?.length) {
            return <NotFoundState fullWidth
                                  headline={<FormattedMessage id='intl-msg:nothingFound'/>}
                                  message={<FormattedMessage id='intl-msg:controlDevices.notFound'/>}/>;
        }

        return (
            <>
            <DeviceListTable
                controlDevices={controlDevices?.content}
                descriptors={devicesDescriptors}
                canUpdateControlDevices={false}
                allowSelection={true}
                onSelectItem={(serialNumber, _) => dispatch(setShowDeviceDetailsSidebar(serialNumber))}
            />
                <LoadMoreButton
                    loaded={controlDevices?.content.length || 0}
                    total={controlDevices?.totalElements || 0}
                    onLoadMore={handleLoadMore}
                    loadMoreMessage={<FormattedMessage id='intl-msg:loadMore'/>}
                    progressBarStyle={'gray'}
                />
            </>
        );
    };

    return (
        <div>
            <div className='margin-bottom-10 display-flex flex-row align-items-center justify-content-between'>
                <div className={'display-flex flex-row align-items-center gap-10'}>
                    <span className={'text-color-gray'}>
                        <FormattedMessage
                            id='intl-msg:totalControlDevices.phrase'
                            values={{
                                totalElements: controlDevices?.totalElements || 0,
                                selectedCount: 0,
                            }}
                        />
                    </span>
                    <div className={'flex-grow-1 min-width-200'}>
                        <TableSearch
                            value={searchValue}
                            onChange={(value) => {
                                setSearchValue(value);
                                updateUrl({ search: value });
                            }}
                            placeholder={intl.formatMessage({ id: 'intl-msg:findSerialOrVin' })}
                        />
                    </div>
                </div>
                <div className={'flex-grow-1 gap-10 display-flex flex-row'}>
                    <SelectOTA
                        options={healthOptions}
                        label={<FormattedMessage id={'intl-msg:deviceHealthState'}/>}
                        onChange={onDeviceStatusTypeChange}
                    />
                    <SelectOTA
                        options={installationOptions}
                        label={<FormattedMessage id={'intl-msg:InstallationStatus'}/>}
                        onChange={onInstallationStatusChange}
                    />
                    <DatePicker
                        clearableInput
                        mandatory={false}
                        closeOnSelect={false}
                        className={'min-width-200'}
                        inputProps={{
                            placeholder: intl.formatMessage({ id: 'intl-msg:lastSeenAfter' }),
                        }}
                        value={lastSeenDate}
                        onChange={onLastSeenChange}
                    />
                    <button
                        className='btn btn-default '
                        onClick={clearFilters}
                    >
                        <span className='rioglyph rioglyph-trash' aria-hidden='true'></span>&nbsp;
                        <FormattedMessage id='intl-msg:clearFilters'/>
                    </button>
                </div>
            </div>
            {renderContent()}

        </div>
    );
};

export default CampaignDevices;


